import {
	type Field,
	type FieldRefs,
	type FieldValues,
	type Path,
	type PathValue,
	set,
	type UseFormReturn,
} from 'react-hook-form'

const isBooleanElement = (element: unknown): element is HTMLInputElement =>
	element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')

const isRadioElement = (element: unknown): element is HTMLInputElement =>
	element instanceof HTMLInputElement && element.type === 'radio'

const isValueElement = (element: unknown): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement =>
	element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement

// react-hook-form refs are not guaranteed to be the actual mounted DOM element.
// Clearing by field name keeps the rendered native inputs in sync even when RHF stores
// a wrapper ref or a ref from another window realm.
const clearNamedElements = (fieldPath: string) => {
	Array.from(document.getElementsByName(fieldPath)).forEach((element) => {
		if (!(element instanceof HTMLElement)) return
		if (isBooleanElement(element)) {
			element.checked = false
			return
		}
		if (isValueElement(element)) element.value = ''
	})
}

/**
 * Build a cleared value payload from RHF's internal field registry.
 *
 * Why this exists:
 * - `methods.reset({})` does not mean "clear everything". If the form has `initialValues`,
 *   RHF will restore those defaults instead.
 * - Some fields in this codebase are controlled components backed by `useController`.
 *   They participate in RHF state, but do not always expose a native ref we can clear directly.
 * - Some native inputs do expose a ref, but RHF's stored ref is not always the live DOM element
 *   that the user currently sees, so updating form state alone can leave stale text in the UI.
 *
 * The traversal therefore does two jobs at once:
 * - derive a concrete payload that represents the cleared form state
 * - best-effort clear any mounted DOM elements with the same field name
 *
 * We derive the field path from RHF's `_fields` tree instead of from `ref.name` because the
 * registry is the stable source of truth for both native and controlled fields.
 */
const clearInput = <T extends FieldValues>(
	methods: UseFormReturn<T, any, any>,
	fields: FieldRefs,
	values: Partial<T>,
	path = '',
) => {
	const { getValues } = methods
	Object.entries(fields)
		.filter((entry): entry is [string, Field] => entry[0] !== '_f' && Boolean(entry[1]))
		.forEach(([key, field]) => {
			const fieldPath = path ? `${path}.${key}` : key
			const { ref } = field._f ?? {}
			// If the input is of any type that is by default a boolean, reset to false
			if (isBooleanElement(ref)) {
				ref.checked = false
				set(values, fieldPath, false as PathValue<T, Path<T>>)
				clearNamedElements(fieldPath)
				// If the field is a text element, reset to an empty string
			} else if (isValueElement(ref)) {
				ref.value = ''
				set(values, fieldPath, '' as PathValue<T, Path<T>>)
				clearNamedElements(fieldPath)
				// Controlled fields do not always have a direct DOM ref, so we infer their clear value from
				// the current form state while still writing the result back to the same field path.
			} else if (field._f) {
				const fieldValue = getValues(fieldPath as Path<T>)
				if (Array.isArray(fieldValue)) set(values, fieldPath, [] as PathValue<T, Path<T>>)
				else if (typeof fieldValue === 'boolean') set(values, fieldPath, false as PathValue<T, Path<T>>)
				else set(values, fieldPath, '' as PathValue<T, Path<T>>)
				clearNamedElements(fieldPath)
			}
			clearInput(methods, field as unknown as FieldRefs, values, fieldPath)
		})
}

/**
 * Return a cleared snapshot of the current form values.
 *
 * The function starts from the current value shape rather than from `{}` so nested objects and
 * mounted field paths stay present in the reset payload. That matters because RHF only updates
 * mounted fields predictably when the reset payload still mirrors the current structure.
 */
export const clearInputs = <T extends FieldValues>(methods: UseFormReturn<T, any, any>) => {
	const values = structuredClone(methods.getValues()) as Partial<T>
	clearInput(methods, methods.control._fields, values)
	return values
}

/**
 * Mirror a value object back into mounted named DOM elements.
 *
 * `clearFields()` manually clears native inputs as part of building the cleared payload.
 * A later `reset()` fixes RHF state immediately, but some native inputs can still keep the old
 * visual value until React or the browser re-syncs them. Writing the values back by field name
 * keeps the visible DOM aligned with RHF after a `clearFields() -> reset()` cycle.
 */
export const syncNamedElements = (values: unknown, path = ''): void => {
	if (values && typeof values === 'object') {
		Object.entries(values).forEach(([key, value]) => {
			syncNamedElements(value, path ? `${path}.${key}` : key)
		})
		return
	}
	if (!path) return
	Array.from(document.getElementsByName(path)).forEach((element) => {
		if (isRadioElement(element)) {
			element.checked = String(values ?? '') === element.value
			return
		}
		if (isBooleanElement(element)) {
			element.checked = Boolean(values)
			return
		}
		if (isValueElement(element)) element.value = values === null || values === undefined ? '' : String(values)
	})
}
